import { useQuery } from "@tanstack/react-query";
import { graphsApi, type GraphRun } from "../../api/graphs";
import { queryKeys } from "../../lib/queryKeys";
import { Clock, CheckCircle2, XCircle, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { useState } from "react";

const STATUS_STYLES: Record<string, { icon: React.ReactNode; label: string; className: string }> = {
  succeeded: {
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    label: "Succeeded",
    className: "text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/20",
  },
  failed: {
    icon: <XCircle className="h-3.5 w-3.5" />,
    label: "Failed",
    className: "text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20",
  },
  running: {
    icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
    label: "Running",
    className: "text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20",
  },
};

function formatDuration(startedAt: string, finishedAt: string | null): string {
  if (!finishedAt) return "In progress";
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
}

function formatTime(date: string): string {
  return new Date(date).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function RunRow({ run, onSelect }: { run: GraphRun; onSelect: (run: GraphRun) => void }) {
  const style = STATUS_STYLES[run.status] ?? STATUS_STYLES.failed;

  return (
    <button
      type="button"
      onClick={() => onSelect(run)}
      className="w-full flex items-center gap-3 px-4 py-3 border-b border-border/30 hover:bg-accent/30 transition-colors text-left"
    >
      <div className={cn("flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium shrink-0", style.className)}>
        {style.icon}
        {style.label}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-foreground truncate">
          {formatTime(run.startedAt)}
        </p>
      </div>
      <div className="flex items-center gap-3 shrink-0 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatDuration(run.startedAt, run.finishedAt)}
        </span>
        {run.totalTokens > 0 && (
          <span>{run.totalTokens.toLocaleString()} tokens</span>
        )}
      </div>
    </button>
  );
}

function RunDetail({ run, onBack }: { run: GraphRun; onBack: () => void }) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border/50 shrink-0">
        <button type="button" onClick={onBack} className="text-xs text-muted-foreground hover:text-foreground">
          &larr; Back
        </button>
        <span className="text-xs font-medium">Run Detail</span>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-auto-hide p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="px-3 py-2 rounded-lg bg-muted/50">
            <p className="text-[10px] text-muted-foreground">Status</p>
            <p className="font-medium">{run.status}</p>
          </div>
          <div className="px-3 py-2 rounded-lg bg-muted/50">
            <p className="text-[10px] text-muted-foreground">Duration</p>
            <p className="font-medium">{formatDuration(run.startedAt, run.finishedAt)}</p>
          </div>
          <div className="px-3 py-2 rounded-lg bg-muted/50">
            <p className="text-[10px] text-muted-foreground">Started</p>
            <p className="font-medium">{formatTime(run.startedAt)}</p>
          </div>
          <div className="px-3 py-2 rounded-lg bg-muted/50">
            <p className="text-[10px] text-muted-foreground">Tokens</p>
            <p className="font-medium">{run.totalTokens.toLocaleString()}</p>
          </div>
        </div>
        {run.events.length > 0 && (
          <div className="mt-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Events</p>
            <div className="space-y-1">
              {run.events.map((event, i) => (
                <EventRow key={i} event={event} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EventRow({ event }: { event: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);
  const type = String(event.type ?? "unknown");
  const data = (event.data ?? undefined) as Record<string, unknown> | undefined;

  const typeColor: Record<string, string> = {
    node_start: "text-blue-400",
    node_end: "text-emerald-400",
    stream: "text-foreground/70",
    error: "text-red-400",
    done: "text-emerald-500",
  };

  return (
    <div className="border border-border/30 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-[11px] hover:bg-accent/30 transition-colors"
      >
        {expanded ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
        <span className={cn("font-mono", typeColor[type] ?? "text-muted-foreground")}>{type}</span>
        {data?.nodeName != null && <span className="text-foreground/70 truncate">{String(data.nodeName)}</span>}
      </button>
      {expanded && data && (
        <div className="px-2.5 pb-2 text-[10px] font-mono text-foreground/70 whitespace-pre-wrap break-all">
          {JSON.stringify(data, null, 2)}
        </div>
      )}
    </div>
  );
}

interface ExecutionHistoryProps {
  graphId: string;
  companyId: string;
}

export function ExecutionHistory({ graphId, companyId }: ExecutionHistoryProps) {
  const [selectedRun, setSelectedRun] = useState<GraphRun | null>(null);

  const { data: runs, isLoading } = useQuery({
    queryKey: [...queryKeys.graphs.detail(graphId), "runs", companyId],
    queryFn: () => graphsApi.listRuns(companyId, graphId),
    refetchInterval: 10000,
    enabled: !!companyId,
  });

  if (selectedRun) {
    return <RunDetail run={selectedRun} onBack={() => setSelectedRun(null)} />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!runs || runs.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        No execution history yet. Run this agent to see past results.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border/50 shrink-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Execution History
        </span>
        <span className="text-[10px] text-muted-foreground tabular-nums">{runs.length} runs</span>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-auto-hide">
        {runs.map((run) => (
          <RunRow key={run.id} run={run} onSelect={setSelectedRun} />
        ))}
      </div>
    </div>
  );
}
